import React from 'react';
import { Button, Checkbox, Chip, FormControlLabel, Stack, Typography } from '@mui/material';

import { DocumentKindGroup } from './types';
import { plural } from '../by-patient/format';

interface Props {
  groups: DocumentKindGroup[];
  loading: boolean;
  deselected: Set<string>;
  onToggle: (kind: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
}

/**
 * SQ-173 step 3 — WHAT, for a cohort.
 *
 * Groups only. Across ninety-odd residents the document list is far too long to be a
 * selection, so this does not expand — the screen points at By patient for document-level
 * control. Counts come from SQ-172, derived from the same query as the total.
 */
export const GroupPicker: React.FC<Props> = ({
  groups,
  loading,
  deselected,
  onToggle,
  onSelectAll,
  onClear,
}) => {
  if (loading) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
      >
        Counting…
      </Typography>
    );
  }

  if (groups.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
      >
        No documents match these filters.
      </Typography>
    );
  }

  return (
    <Stack spacing={0.25}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ pt: 0.5, pl: 0.5 }}
      >
        <Button
          size="small"
          sx={{ minWidth: 0, p: 0, fontWeight: 700 }}
          onClick={onSelectAll}
        >
          Select all
        </Button>
        <Button
          size="small"
          color="inherit"
          sx={{ minWidth: 0, p: 0, fontWeight: 700, color: 'text.secondary' }}
          onClick={onClear}
        >
          Clear
        </Button>
      </Stack>
      {groups.map((g) => (
        <FormControlLabel
          key={g.kind}
          sx={{ ml: 0 }}
          control={
            <Checkbox
              size="small"
              checked={!deselected.has(g.kind)}
              //  A kind with nothing in it is shown greyed with its zero rather than
              //  hidden — hiding it makes people wonder where it went (D8).
              disabled={g.documentCount === 0}
              onChange={() => onToggle(g.kind)}
              inputProps={{ 'aria-label': `${g.label} group` }}
            />
          }
          label={
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 500 }}
              >
                {g.label}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={g.documentCount}
              />
            </Stack>
          }
        />
      ))}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ pt: 1 }}
      >
        Need to pick documents one by one? Use <strong>One patient</strong> — across a group the
        list is too long to choose from.
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
      >
        {plural(
          groups.filter((g) => !deselected.has(g.kind)).reduce((n, g) => n + g.documentCount, 0),
          'document'
        )}{' '}
        selected
      </Typography>
    </Stack>
  );
};
